import React from "react";
import "./PaymentForm.css";
import { useAuth } from "../../../context/AuthContext";
import { REWARD_DISCOUNT_MAX_RATE, getWallet } from "../../Pages/Users/ProfileLogic";

export { REWARD_DISCOUNT_MAX_RATE };

export function getCurrentWalletBalances(userEmail) {
  if (!userEmail) {
    return { redeemable: 0, nonRedeemable: 0, total: 0 };
  }
  const wallet = getWallet(userEmail);
  const redeemable = wallet.redeemable?.balance ?? 0;
  const nonRedeemable = wallet.nonRedeemable?.balance ?? 0;
  return {
    redeemable,
    nonRedeemable,
    total: redeemable + nonRedeemable,
  };
}

// ── Shared payment methods list ───────────────────────────────
export const PAYMENT_METHODS_LIST = [
  { id: "debit",      label: "Debit Card",  icon: "💳", desc: "Visa, Mastercard, RuPay Debit",    fee: 0  },
  { id: "credit",     label: "Credit Card", icon: "💳", desc: "Visa, Mastercard, Amex Credit",     fee: 18 },
  { id: "upi",        label: "UPI",         icon: "📱", desc: "Google Pay, PhonePe, Paytm, BHIM",  fee: 0  },
  { id: "netbanking", label: "Net Banking", icon: "🏦", desc: "All major Indian banks",             fee: 9  },
  { id: "reward",     label: "Pay by Reward", icon: "🎁", desc: "Use your wallet balance",          fee: 0  },
];

// ── Default blank payment details ────────────────────────────
export const PAYMENT_DETAILS_DEFAULT = {
  nameOnCard: "",
  cardNum:    "",
  expiry:     "",
  cvv:        "",
  upiId:      "",
  bankName:   "",
  accountNum: "",
  ifsc:       "",
};

// ── Indian banks for Net Banking ─────────────────────────────
export const BANKS = [
  "State Bank of India",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "Bank of India",
  "IndusInd Bank",
  "Yes Bank",
  "IDFC First Bank",
  "Federal Bank",
  "RBL Bank",
  "South Indian Bank",
  "Karur Vysya Bank",
  "City Union Bank",
  "Bandhan Bank",
  "UCO Bank",
  "Indian Bank",
  "Indian Overseas Bank",
  "Central Bank of India",
  "Saraswat Bank",
  "Jammu & Kashmir Bank",
];

// ── Validation helpers (exported for use in parent) ──────────
export function validatePaymentDetails(method, details) {
  if (!method) return "Please select a payment method.";

  if (method === "debit" || method === "credit") {
    if (!details.nameOnCard?.trim())
      return "Name on card is required.";
    const raw = (details.cardNum || "").replace(/\s/g, "");
    if (!/^\d{16}$/.test(raw))
      return "Please enter a valid 16-digit card number.";
    if (!/^\d{2}\/\d{2}$/.test(details.expiry || ""))
      return "Please enter expiry in MM/YY format.";
    if (!/^\d{3,4}$/.test(details.cvv || ""))
      return "Please enter a valid CVV (3–4 digits).";
  }

  if (method === "upi") {
    if (!details.upiId?.trim())
      return "Please enter your UPI ID.";
    if (!/^[\w.\-_+]+@[a-zA-Z]{2,}$/.test(details.upiId.trim()))
      return "Please enter a valid UPI ID (e.g. name@upi).";
  }

  if (method === "netbanking") {
    if (!details.bankName)
      return "Please select your bank.";
    if (!details.accountNum?.trim())
      return "Bank account number is required.";
    if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/i.test(details.ifsc || ""))
      return "Please enter a valid IFSC code (e.g. SBIN0001234).";
  }

  return null;
}

export function roundReward(val) {
  const floor = Math.floor(val);
  const frac = val - floor;
  return frac > 0.5 ? Math.ceil(val) : floor;
}

// ── Auto-format card number with spaces (XXXX XXXX XXXX XXXX) ─
function formatCardNum(raw) {
  return raw.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}

// ── Auto-format expiry MM/YY ──────────────────────────────────
function formatExpiry(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length > 2) return digits.slice(0, 2) + "/" + digits.slice(2);
  return digits;
}

// ── Auto-format IFSC to uppercase ────────────────────────────
function formatIFSC(raw) {
  return raw.toUpperCase().slice(0, 11);
}

// ─────────────────────────────────────────────────────────────
//  PaymentForm component
//
//  Props:
//    selectedMethod   : string  — "debit" | "credit" | "upi" | "netbanking"
//    onMethodSelect   : (id: string) => void
//    details          : PAYMENT_DETAILS_DEFAULT shape
//    onDetailChange   : standard React onChange event handler  (e) => void
//                       (parent uses setF-style updater)
// ─────────────────────────────────────────────────────────────
export default function PaymentForm({ selectedMethod, onMethodSelect, details, onDetailChange, walletBalances, rewardLocked, hideMethods = [], requiredAmount = 0, onRewardPay }) {
  const { user } = useAuth();
  const totalBalance = walletBalances ? (walletBalances.redeemable ?? 0) + (walletBalances.nonRedeemable ?? 0) : 0;

  function handleCardNum(e) {
    const formatted = formatCardNum(e.target.value);
    onDetailChange({ target: { name: "cardNum", value: formatted } });
  }

  function handleExpiry(e) {
    const formatted = formatExpiry(e.target.value);
    onDetailChange({ target: { name: "expiry", value: formatted } });
  }

  function handleIFSC(e) {
    const formatted = formatIFSC(e.target.value);
    onDetailChange({ target: { name: "ifsc", value: formatted } });
  }

  return (
    <div className="pf-wrap">

      {/* ── Method tabs ── */}
      <div className="pf-methods">
        {PAYMENT_METHODS_LIST.filter(m => !hideMethods.includes(m.id)).map(m => {
          const isReward = m.id === "reward";
          const isLoggedIn = !!user;
          const isLocked = isReward && (!isLoggedIn || rewardLocked || (requiredAmount > 0 && totalBalance < requiredAmount));
          return (
            <button
              key={m.id}
              type="button"
              className={`pf-method-btn${selectedMethod === m.id ? " pf-method-btn--active" : ""}${isLocked ? " pf-method-btn--locked" : ""}`}
              onClick={() => !isLocked && onMethodSelect(m.id)}
              disabled={isLocked}
              title={isLocked ? (!isLoggedIn ? "Please log in to use Pay by Reward" : `Insufficient balance — need ₹${requiredAmount.toLocaleString("en-IN")} but have ₹${totalBalance.toLocaleString("en-IN")}`) : ""}
            >
              <span className="pf-method-label">{m.label}</span>
              {isLocked
                ? <span className="pf-method-locked-badge">Locked</span>
                : m.fee > 0
                  ? <span className="pf-method-fee">₹{m.fee}</span>
                  : <span className="pf-method-free">Free</span>
              }
            </button>
          );
        })}
      </div>

      {/* ── Debit / Credit Card fields ── */}
      {(selectedMethod === "debit" || selectedMethod === "credit") && (
        <div className="pf-fields">
          <div className="pf-fields-title">
            {selectedMethod === "debit" ? "Debit Card Details" : "Credit Card Details"}
          </div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">Name on Card *</label>
            <input
              className="pf-input"
              name="nameOnCard"
              value={details.nameOnCard || ""}
              onChange={onDetailChange}
              placeholder="As printed on card"
              autoComplete="cc-name"
            />
          </div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">Card Number *</label>
            <input
              className="pf-input pf-input--mono"
              name="cardNum"
              value={details.cardNum || ""}
              onChange={handleCardNum}
              placeholder="XXXX  XXXX  XXXX  XXXX"
              maxLength={19}
              autoComplete="cc-number"
              inputMode="numeric"
            />
          </div>

          <div className="pf-field-row">
            <div className="pf-field-group">
              <label className="pf-label">Expiry *</label>
              <input
                className="pf-input pf-input--mono"
                name="expiry"
                value={details.expiry || ""}
                onChange={handleExpiry}
                placeholder="MM / YY"
                maxLength={5}
                autoComplete="cc-exp"
                inputMode="numeric"
              />
            </div>
            <div className="pf-field-group">
              <label className="pf-label">CVV *</label>
              <input
                className="pf-input pf-input--mono"
                name="cvv"
                value={details.cvv || ""}
                onChange={onDetailChange}
                placeholder="• • •"
                maxLength={4}
                type="password"
                autoComplete="cc-csc"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="pf-secure-note">🔒 Your card details are encrypted and secure</div>
        </div>
      )}

      {/* ── UPI fields ── */}
      {selectedMethod === "upi" && (
        <div className="pf-fields">
          <div className="pf-fields-title">UPI Payment</div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">UPI ID *</label>
            <input
              className="pf-input"
              name="upiId"
              value={details.upiId || ""}
              onChange={onDetailChange}
              placeholder="yourname@upi  (e.g. 9876543210@okaxis)"
              autoComplete="off"
            />
          </div>

          <div className="pf-upi-apps">
            <span className="pf-upi-apps-label">Accepted via:</span>
            {["Google Pay", "PhonePe", "Paytm", "BHIM", "Amazon Pay"].map(app => (
              <span key={app} className="pf-upi-app-badge">{app}</span>
            ))}
          </div>

          <div className="pf-secure-note">🔒 You will receive a payment request on your UPI app</div>
        </div>
      )}

      {/* ── Net Banking fields ── */}
      {selectedMethod === "netbanking" && (
        <div className="pf-fields">
          <div className="pf-fields-title">Net Banking Details</div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">Select Bank *</label>
            <select
              className="pf-input pf-select"
              name="bankName"
              value={details.bankName || ""}
              onChange={onDetailChange}
            >
              <option value="">— Select your bank —</option>
              {BANKS.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">Account Number *</label>
            <input
              className="pf-input pf-input--mono"
              name="accountNum"
              value={details.accountNum || ""}
              onChange={onDetailChange}
              placeholder="Your bank account number"
              inputMode="numeric"
              autoComplete="off"
            />
          </div>

          <div className="pf-field-group pf-field-group--full">
            <label className="pf-label">IFSC Code *</label>
            <input
              className="pf-input pf-input--upper"
              name="ifsc"
              value={details.ifsc || ""}
              onChange={handleIFSC}
              placeholder="e.g. SBIN0001234"
              maxLength={11}
              autoComplete="off"
            />
          </div>

          <div className="pf-secure-note">🔒 Your banking details are encrypted and never stored on our servers</div>
        </div>
      )}

      {selectedMethod === "reward" && (
        onRewardPay ? (
          <div className="pf-fields">
            <div className="pf-fields-title">Pay by Reward</div>

            <div
              className={`pf-pay-card${totalBalance >= requiredAmount ? " pf-pay-card--eligible" : ""}`}
              onClick={() => onMethodSelect("reward")}
            >
              <div className="pf-pay-card-row">
                <span>Required Amount</span>
                <strong>₹{requiredAmount.toLocaleString("en-IN")}</strong>
              </div>
              <div className="pf-pay-card-row">
                <span>Total Reward</span>
                <strong>₹{totalBalance.toLocaleString("en-IN")}</strong>
              </div>
              {totalBalance >= requiredAmount ? (
                <span className="pf-pay-card-badge">Pay by Reward ✓</span>
              ) : (
                <span className="pf-pay-card-badge pf-pay-card-badge--ineligible">Insufficient Balance</span>
              )}
            </div>

            <div className="pf-secure-note">🔒 Rewards are securely managed in your wallet</div>
          </div>
        ) : (
          <div className="pf-fields">
            <div className="pf-fields-title">Pay by Reward</div>

            <div className="pf-reward-balance-card">
              <div className="pf-reward-row">
                <span className="pf-reward-label">Redeemable Balance</span>
                <span className="pf-reward-value">₹{roundReward(walletBalances?.redeemable ?? 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="pf-reward-row">
                <span className="pf-reward-label">Non-Redeemable Balance</span>
                <span className="pf-reward-value">₹{roundReward(walletBalances?.nonRedeemable ?? 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="pf-reward-divider" />
              <div className="pf-reward-row pf-reward-row--total">
                <span className="pf-reward-label">Total Available</span>
                <span className="pf-reward-value">₹{roundReward((walletBalances?.redeemable ?? 0) + (walletBalances?.nonRedeemable ?? 0)).toLocaleString("en-IN")}</span>
              </div>
            </div>

            <div className="pf-reward-note">
              Payment will be deducted from your redeemable balance first. If insufficient, the remaining amount will be taken from your non-redeemable balance.
            </div>

            <div className="pf-secure-note">🔒 Rewards are securely managed in your wallet</div>
          </div>
        )
      )}

      {!selectedMethod && (
        <div className="pf-no-method">Select a payment method above to continue</div>
      )}

    </div>
  );
}

export function RewardDiscountSection({
  selectedMethod,
  baseApplicationFee = 0,
  walletBalances = null,
  discountAmount = 0,
  onDiscountChange,
  onOpenLogin,
  disabled = false,
}) {
  const { user } = useAuth();
  const isLoggedIn = !!user?.email;

  const balances = walletBalances || (isLoggedIn ? getCurrentWalletBalances(user?.email) : { redeemable: 0, nonRedeemable: 0, total: 0 });
  const maxDiscount = Math.round(baseApplicationFee * REWARD_DISCOUNT_MAX_RATE);
  const effectiveMax = maxDiscount > 0 ? maxDiscount : 0;

  const exceedsBalance = discountAmount > balances.total;
  const hasError = exceedsBalance;

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const num = parseInt(raw, 10) || 0;
    
    let capped = num;
    if (effectiveMax > 0 && num > effectiveMax) {
      capped = effectiveMax;
    }
    
    if (onDiscountChange) {
      onDiscountChange(capped);
    }
  };

  if (selectedMethod === "reward") {
    return null;
  }

  return (
    <div className="pf-reward-discount-section">
      <div className="pf-reward-discount-header">
        <span className="pf-rd-title">Reward Discount</span>
         {maxDiscount > 0 && (
           <span className="pf-rd-max-note">Max: {Math.round(REWARD_DISCOUNT_MAX_RATE * 100)}%</span>
         )}
      </div>

      {!isLoggedIn ? (
        <div className="pf-rd-locked">
          <span className="pf-rd-lock-icon">🔒</span>
          <span className="pf-rd-locked-text">
            <button type="button" className="pf-rd-signin-btn" onClick={onOpenLogin} disabled={disabled}>
              Sign in
            </button>
            &nbsp;to use the Discount
          </span>
        </div>
      ) : (
        <>
         <div className="pf-rd-input-row">
           <label className="pf-rd-label">Amount</label>
           <div className="pf-rd-input-wrap">
             <span className="pf-rd-rupee">₹</span>
              <input
                type="text"
                className={`pf-input pf-rd-input ${hasError ? "pf-rd-input--error" : ""}`}
                value={discountAmount > 0 ? discountAmount.toString() : ""}
                onChange={handleInputChange}
                placeholder={maxDiscount > 0 ? maxDiscount.toString() : "0"}
                disabled={disabled}
                inputMode="numeric"
              />
           </div>
         </div>

         <div className="pf-rd-row">
           <span className="pf-rd-label">Max Discount Amount</span>
           <span className={`pf-rd-value ${hasError ? "pf-rd-value--error" : ""}`}>
             ₹{maxDiscount.toLocaleString("en-IN")}
           </span>
         </div>

         {hasError && exceedsBalance && (
           <div className="pf-rd-error-row">
             <span className="pf-rd-error">Insufficient balance</span>
           </div>
         )}

          <div className="pf-rd-divider" />

          <div className="pf-rd-row">
            <span className="pf-rd-label pf-rd-label--balance">Redeemable Balance</span>
            <span className="pf-rd-balance-value">₹{roundReward(balances.redeemable).toLocaleString("en-IN")}</span>
          </div>

          <div className="pf-rd-row">
            <span className="pf-rd-label pf-rd-label--balance">Non-Redeemable Balance</span>
            <span className="pf-rd-balance-value">₹{roundReward(balances.nonRedeemable).toLocaleString("en-IN")}</span>
          </div>
        </>
      )}
    </div>
  );
}
